import { useMemo } from "react";
import { motion } from "framer-motion";
import { Target, MessageCircle, HelpCircle, type LucideIcon } from "lucide-react";
import { AnimatedNumber } from "@/components/dashboard/AnimatedNumber";
import { MiniSparkline } from "@/components/dashboard/MiniSparkline";
import { cn } from "@/lib/cn";

export type DailyCount = { date: string; count: number };

export type ThisWeekStripProps = {
  /** Average quiz percent across chapters quizzed this week. 0 if none. */
  avgQuizScore: number;
  /** Per-attempt scores in chronological order this week. Drives the
   *  Avg Score tile's sparkline. */
  quizScoreValues: number[];
  chatMessages: number;
  quizAttempts: number;
  /** Per-day chat-message counts for the last 7 days (sparse, only days
   *  with activity). When present, drives the Tutor tile's real sparkline;
   *  when absent (older backend) we fall back to a flat total/7 curve. */
  dailyChatMessages?: DailyCount[];
  /** Per-day quiz-attempt counts for the last 7 days (sparse). Same
   *  present/absent fallback contract as dailyChatMessages. */
  dailyQuizAttempts?: DailyCount[];
};

/**
 * The backend already returns a dense, IST-bucketed 7-element window, oldest
 * first. This used to rebuild the window from the browser clock in UTC, which
 * put the sparkline a column out of step with the numbers above it whenever an
 * IST browser loaded the page before 05:30.
 */
function toWeeklyCounts(daily: DailyCount[]): number[] {
  return daily.map((d) => Math.max(0, Math.round(d.count)));
}

/**
 * Three vertical stat tiles -- "avg score / messages / quizzes" --
 * each with a sparkline along the bottom edge. Numbers tween on data
 * invalidation; sparklines plot in left-to-right after the number.
 *
 * The Study time tile lives in its own dedicated card below this row
 * (StudyTimeChart) -- we keep the headline number + curve big over
 * there rather than duplicating it as a small tile here. Avg quiz
 * score takes the freed slot; it complements the QuizPerformance
 * card on the right rail (which is per-chapter), giving the user a
 * single week-level summary at a glance.
 *
 * When the dashboard endpoint returns per-day breakdowns
 * (daily_chat_messages / daily_quiz_attempts), the Tutor + Quizzes tiles
 * plot the real daily counts. On older backends those arrays are absent,
 * so we fall back to a flat total/7 placeholder curve -- it isn't
 * dead-zero (visually consistent with the Avg Score tile) but doesn't
 * fake a "shape" we don't have data for. The Avg Score tile always plots
 * the actual per-attempt scores this week.
 */
export function ThisWeekStrip({
  avgQuizScore,
  quizScoreValues,
  chatMessages,
  quizAttempts,
  dailyChatMessages,
  dailyQuizAttempts,
}: ThisWeekStripProps) {
  // Prefer the real per-day breakdown when the backend provides it; fall
  // back to a flat total/7 curve when the arrays are absent (older deploy).
  const chatSpark = useMemo(
    () =>
      dailyChatMessages
        ? toWeeklyCounts(dailyChatMessages)
        : Array.from({ length: 7 }, () => chatMessages / 7),
    [dailyChatMessages, chatMessages],
  );
  const quizSpark = useMemo(
    () =>
      dailyQuizAttempts
        ? toWeeklyCounts(dailyQuizAttempts)
        : Array.from({ length: 7 }, () => quizAttempts / 7),
    [dailyQuizAttempts, quizAttempts],
  );

  // For the Avg Score sparkline: pad to at least 4 points so a single
  // attempt still draws a meaningful curve instead of a single dot.
  const scoreSpark = useMemo(() => {
    if (quizScoreValues.length === 0) return [0, 0, 0, 0];
    if (quizScoreValues.length >= 4) return quizScoreValues;
    return [...quizScoreValues, ...Array(4 - quizScoreValues.length).fill(quizScoreValues[quizScoreValues.length - 1])];
  }, [quizScoreValues]);

  return (
    <div>
      <header className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-sm font-semibold tracking-tight text-text-muted uppercase tracking-[0.14em]">
          This week
        </h3>
        <span className="text-[11px] text-text-subtle">last 7 days</span>
      </header>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Tile
          icon={Target}
          label="Avg score"
          labelShort="Avg"
          delayIndex={0}
          value={avgQuizScore}
          format={(n) => `${Math.max(0, Math.round(n))}%`}
          sparkValues={scoreSpark}
          sparkMinDomain={100}
        />
        <Tile
          icon={MessageCircle}
          label={chatMessages === 1 ? "Tutor message" : "Tutor messages"}
          labelShort="Tutor"
          delayIndex={1}
          value={chatMessages}
          format={(n) => Math.max(0, Math.round(n)).toString()}
          sparkValues={chatSpark}
          sparkMinDomain={1}
        />
        <Tile
          icon={HelpCircle}
          label={quizAttempts === 1 ? "Quiz" : "Quizzes"}
          labelShort={quizAttempts === 1 ? "Quiz" : "Quizzes"}
          delayIndex={2}
          value={quizAttempts}
          format={(n) => Math.max(0, Math.round(n)).toString()}
          sparkValues={quizSpark}
          sparkMinDomain={1}
        />
      </div>
    </div>
  );
}

function Tile({
  icon: Icon,
  label,
  labelShort,
  value,
  format,
  sparkValues,
  sparkMinDomain,
  delayIndex,
}: {
  icon: LucideIcon;
  label: string;
  labelShort: string;
  value: number;
  format: (n: number) => string;
  sparkValues: number[];
  sparkMinDomain: number;
  delayIndex: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: delayIndex * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "rounded-xl border border-border bg-surface px-2.5 sm:px-4 pt-2.5 sm:pt-4 pb-0",
        "overflow-hidden min-w-0",
        "transition-colors duration-150 hover:bg-surface-2",
      )}
    >
      <div className="flex items-center gap-1 sm:gap-1.5 text-text-subtle min-w-0">
        <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
        <span className="sm:hidden text-[10px] uppercase tracking-[0.06em] truncate">
          {labelShort}
        </span>
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.12em] truncate">
          {label}
        </span>
      </div>
      <AnimatedNumber
        value={value}
        format={format}
        durationMs={520}
        className="block mt-1 sm:mt-1.5 font-display text-lg sm:text-[28px] font-bold tabular-nums tracking-tight truncate"
      />
      <MiniSparkline
        values={sparkValues}
        minDomain={sparkMinDomain}
        delaySec={0.35 + delayIndex * 0.08}
        className="mt-2 sm:mt-3 h-7 sm:h-10"
      />
    </motion.div>
  );
}
